import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShoppingBag, Type, Palette, Truck, Plus, Minus, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function TegelDesignerPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [config, setConfig] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    const [tileColor, setTileColor] = useState(null);
    const [textColor, setTextColor] = useState(null);
    const [fontId, setFontId] = useState("playfair");
    const [text, setText] = useState("Welkom\nthuis");
    const [textScale, setTextScale] = useState(1.0);   // 0.5 - 2.0
    const [textFinish, setTextFinish] = useState("mat"); // "mat" | "glans"
    const [notes, setNotes] = useState("");
    const [qty, setQty] = useState(1);
    const [adding, setAdding] = useState(false);
    const [referencePhoto, setReferencePhoto] = useState(null); // {url,name} van gekozen template met foto
    const previewRef = useRef(null);

    useEffect(() => {
        Promise.all([
            api.get("/custom-tile/config"),
            api.get("/custom-tile/templates").catch(() => ({ data: [] })),
        ]).then(([cfgRes, tplRes]) => {
            const data = cfgRes.data;
            setConfig(data);
            setTileColor(data.tile_colors[0]);
            setTextColor(data.text_colors[1] || data.text_colors[0]);
            setTemplates(tplRes.data || []);
            setLoading(false);
        }).catch(() => {
            toast.error("Kon tegel-config niet laden");
            setLoading(false);
        });
    }, []);

    const applyTemplate = (tpl) => {
        if (!config) return;
        setText(tpl.text || "");
        setFontId(tpl.font || "playfair");
        setTextScale(Math.max(0.5, Math.min(2.0, tpl.text_scale || 1.0)));
        // Match tile color (fallback: maak een ad-hoc object)
        const matchedTile = config.tile_colors.find(c => c.hex.toUpperCase() === (tpl.tile_color || "").toUpperCase())
            || { name: tpl.tile_color_name, hex: tpl.tile_color };
        setTileColor(matchedTile);
        const matchedText = config.text_colors.find(c => c.hex.toUpperCase() === (tpl.text_color || "").toUpperCase())
            || { name: tpl.text_color_name, hex: tpl.text_color };
        setTextColor(matchedText);
        setReferencePhoto(tpl.image_url ? { url: tpl.image_url, name: tpl.name } : null);
        toast.success(`Voorbeeld "${tpl.name}" toegepast`);
    };

    const fontCss = useMemo(() => {
        if (!config) return "'Playfair Display', serif";
        const f = config.fonts.find(x => x.id === fontId) || config.fonts[0];
        return f.css;
    }, [fontId, config]);

    const generatePreview = () => {
        if (!tileColor || !textColor) return "";
        try {
            const size = 512;
            const canvas = document.createElement("canvas");
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = tileColor.hex;
            ctx.fillRect(0, 0, size, size);
            ctx.strokeStyle = "rgba(0,0,0,0.08)";
            ctx.lineWidth = 6;
            ctx.strokeRect(3, 3, size - 6, size - 6);
            const lines = (text || "").split("\n").slice(0, 8);
            const baseFontSize = Math.floor(size / (lines.length + 1.6));
            const fontSize = Math.max(14, Math.floor(baseFontSize * textScale));
            ctx.fillStyle = textColor.hex;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.font = `700 ${fontSize}px ${fontCss}`;
            const lineHeight = fontSize * 1.1;
            const totalH = lineHeight * lines.length;
            const startY = (size - totalH) / 2 + lineHeight / 2;
            lines.forEach((ln, i) => {
                ctx.fillText(ln, size / 2, startY + i * lineHeight);
            });
            return canvas.toDataURL("image/png", 0.85);
        } catch {
            return "";
        }
    };

    const addToCart = async () => {
        if (!user) {
            toast.info("Log in om toe te voegen aan je winkelmandje");
            navigate("/login?next=/tegel-designer");
            return;
        }
        if (!text.trim()) {
            toast.error("Vul eerst een tekst in");
            return;
        }
        if (text.length > 200) {
            toast.error("Tekst max 200 tekens");
            return;
        }
        setAdding(true);
        try {
            const preview = generatePreview();
            await api.post("/cart/items/custom-tile", {
                tile_color: tileColor.hex,
                tile_color_name: tileColor.name,
                text: text.trim(),
                text_color: textColor.hex,
                text_color_name: textColor.name,
                font: fontId,
                text_scale: textScale,
                text_finish: textFinish,
                quantity: qty,
                preview_data_url: preview,
                notes: notes.trim(),
            });
            toast.success("Tegeltje toegevoegd aan je winkelmandje");
            navigate("/cart");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        } finally {
            setAdding(false);
        }
    };

    if (loading || !config || !tileColor || !textColor) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-zinc-50">
                <Loader2 className="animate-spin text-zinc-400" />
            </div>
        );
    }

    const totalPrice = (config.price * qty).toFixed(2);

    return (
        <div data-testid="tegel-designer-page" className="bg-zinc-50 min-h-screen">
            {/* Hero */}
            <section className="border-b border-zinc-200 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
                    <p className="label-tiny text-emerald-700">★ Personaliseerbaar cadeau</p>
                    <h1 className="font-display text-4xl md:text-5xl mt-2 tracking-tight">
                        Tegeltje met <span className="text-emerald-700 italic">eigen tekst</span>.
                    </h1>
                    <p className="text-zinc-600 mt-3 max-w-2xl text-sm leading-relaxed">
                        Keramisch tegeltje van <strong>15×15 cm</strong> inclusief stijlvolle standaard.
                        Kies een tegelkleur, tekstkleur en lettertype — schrijf wat je wil. Wij maken er een uniek cadeau van.
                    </p>
                    <div className="mt-4 flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
                        <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-600" /> Inclusief tegelstandaard</span>
                        <span className="inline-flex items-center gap-1.5"><Truck size={14} className="text-amber-600" /> Levertijd 3-7 werkdagen</span>
                        <span className="inline-flex items-center gap-1.5"><Sparkles size={14} className="text-rose-600" /> Voorbeeld per e-mail vóór productie</span>
                    </div>
                </div>
            </section>

            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Live preview */}
                <div className="lg:col-span-5 lg:sticky lg:top-24 self-start">
                    <p className="label-tiny mb-3">Live voorbeeld</p>
                    <div className="border border-zinc-200 bg-white p-8 lg:p-12 flex flex-col items-center">
                        <div
                            ref={previewRef}
                            data-testid="tile-preview"
                            className="w-full aspect-square border-2 shadow-lg flex items-center justify-center p-6 transition-colors"
                            style={{ background: tileColor.hex, borderColor: "rgba(0,0,0,0.12)" }}
                        >
                            <span
                                className="text-center leading-tight whitespace-pre-line break-words w-full"
                                style={{
                                    fontFamily: fontCss,
                                    color: textColor.hex,
                                    fontWeight: 700,
                                    fontSize: `clamp(${10 * textScale}px, ${Math.max(28 - (text.length / 6), 10) * textScale}px, ${56 * textScale}px)`,
                                }}
                            >
                                {text || "Jouw tekst hier"}
                            </span>
                        </div>
                        {/* Houten standaard hint */}
                        <div className="w-3/4 h-3 bg-gradient-to-b from-amber-900 to-amber-950 mt-1 shadow-md rounded-b-sm" />
                        <p className="label-tiny mt-5 text-zinc-400">15 × 15 cm · keramiek · standaard incl.</p>

                        {/* Referentie-foto van het geselecteerde voorbeeld (indien beschikbaar) */}
                        {referencePhoto && (
                            <div data-testid="tile-reference-photo" className="mt-6 w-full border-t border-zinc-100 pt-5">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="label-tiny">Referentie · foto van &ldquo;{referencePhoto.name}&rdquo;</p>
                                    <button
                                        type="button"
                                        onClick={() => setReferencePhoto(null)}
                                        className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-950 underline">
                                        Verberg
                                    </button>
                                </div>
                                <div className="aspect-square w-full max-w-[280px] mx-auto border border-zinc-200 overflow-hidden bg-zinc-50">
                                    <img
                                        src={referencePhoto.url}
                                        alt={`Referentie ${referencePhoto.name}`}
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.style.display = "none"; }}
                                    />
                                </div>
                                <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
                                    Zo ziet de afgewerkte tegel er ongeveer uit. Jouw tekst hierboven bepaalt het eindresultaat.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Configurator */}
                <div className="lg:col-span-7 space-y-6">
                    {/* Voorbeelden / Inspiratie */}
                    {templates.length > 0 && (
                        <section className="border border-zinc-200 bg-white p-6" data-testid="tile-templates-section">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Sparkles size={16} strokeWidth={1.5} />
                                    <p className="label-tiny">Inspiratie — klik om toe te passen</p>
                                </div>
                                <span className="text-[10px] font-mono-dm text-zinc-400">{templates.length} voorbeelden</span>
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                {templates.map(tpl => {
                                    const tplFontCss = (config.fonts.find(f => f.id === tpl.font) || config.fonts[0]).css;
                                    const hasPhoto = !!tpl.image_url;
                                    return (
                                        <button
                                            key={tpl.id}
                                            type="button"
                                            onClick={() => applyTemplate(tpl)}
                                            data-testid={`tile-template-${tpl.id}`}
                                            title={tpl.name}
                                            className="group border border-zinc-200 hover:border-zinc-950 transition-colors overflow-hidden text-left"
                                        >
                                            <div className="aspect-square flex items-center justify-center p-0 overflow-hidden" style={{ background: hasPhoto ? "#F5EDE0" : tpl.tile_color }}>
                                                {hasPhoto ? (
                                                    <img
                                                        src={tpl.image_url}
                                                        alt={tpl.name}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => { e.target.style.display = "none"; }}
                                                    />
                                                ) : (
                                                    <span
                                                        className="text-center leading-tight whitespace-pre-line p-2"
                                                        style={{
                                                            fontFamily: tplFontCss,
                                                            color: tpl.text_color,
                                                            fontWeight: 700,
                                                            fontSize: `${10 * (tpl.text_scale || 1)}px`,
                                                        }}
                                                    >
                                                        {tpl.text}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[10px] uppercase tracking-widest text-zinc-600 truncate px-2 py-1.5 border-t border-zinc-100 group-hover:bg-zinc-50">
                                                {tpl.name}
                                                {tpl.price != null && (
                                                    <span className="ml-1 font-mono-dm text-rose-700">· €{Number(tpl.price).toFixed(2)}</span>
                                                )}
                                            </p>
                                        </button>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Tekst */}
                    <section className="border border-zinc-200 bg-white p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Type size={16} strokeWidth={1.5} />
                            <p className="label-tiny">Stap 01 — Tekst</p>
                        </div>
                        <textarea
                            data-testid="tile-text-input"
                            className="input-flat min-h-[100px] resize-y"
                            value={text}
                            onChange={(e) => setText(e.target.value.slice(0, 200))}
                            placeholder="Bv. Welkom thuis · Mama 60 · I love you · Familie is alles"
                            maxLength={200}
                        />
                        <div className="flex items-center justify-between mt-2 text-[11px] text-zinc-500">
                            <span>Gebruik <kbd className="px-1 bg-zinc-100 border border-zinc-200">Enter</kbd> voor een nieuwe regel</span>
                            <span className="font-mono-dm">{text.length}/200</span>
                        </div>

                        {/* Tekstgrootte slider */}
                        <div className="mt-5 pt-5 border-t border-zinc-100">
                            <div className="flex items-center justify-between mb-2">
                                <p className="label-tiny">Tekstgrootte</p>
                                <span className="text-[11px] font-mono-dm">{Math.round(textScale * 100)}%</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    data-testid="tile-text-smaller"
                                    onClick={() => setTextScale(s => Math.max(0.5, +(s - 0.1).toFixed(2)))}
                                    className="w-9 h-9 border border-zinc-200 flex items-center justify-center hover:bg-zinc-50 text-sm font-bold">A−</button>
                                <input
                                    type="range"
                                    min="0.5" max="2.0" step="0.05"
                                    value={textScale}
                                    onChange={(e) => setTextScale(parseFloat(e.target.value))}
                                    data-testid="tile-text-scale"
                                    className="flex-1 accent-rose-600"
                                />
                                <button
                                    type="button"
                                    data-testid="tile-text-bigger"
                                    onClick={() => setTextScale(s => Math.min(2.0, +(s + 0.1).toFixed(2)))}
                                    className="w-9 h-9 border border-zinc-200 flex items-center justify-center hover:bg-zinc-50 text-base font-bold">A+</button>
                                <button
                                    type="button"
                                    onClick={() => setTextScale(1.0)}
                                    className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-950 underline"
                                >Reset</button>
                            </div>
                        </div>
                    </section>

                    {/* Lettertype */}
                    <section className="border border-zinc-200 bg-white p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles size={16} strokeWidth={1.5} />
                            <p className="label-tiny">Stap 02 — Lettertype</p>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {config.fonts.map(f => (
                                <button
                                    key={f.id}
                                    type="button"
                                    data-testid={`tile-font-${f.id}`}
                                    onClick={() => setFontId(f.id)}
                                    className={`border px-3 py-3 text-left transition-colors ${fontId === f.id ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white hover:border-zinc-400"}`}
                                >
                                    <span className="block text-base font-bold truncate" style={{ fontFamily: f.css }}>Abc</span>
                                    <span className="block text-[10px] uppercase tracking-widest opacity-70 mt-1">{f.label}</span>
                                </button>
                            ))}
                        </div>
                    </section>

                    {/* Tegelkleur */}
                    <section className="border border-zinc-200 bg-white p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Palette size={16} strokeWidth={1.5} />
                            <p className="label-tiny">Stap 03 — Tegelkleur</p>
                        </div>
                        <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                            {config.tile_colors.map(c => (
                                <button
                                    key={c.hex}
                                    type="button"
                                    data-testid={`tile-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => setTileColor(c)}
                                    title={c.name}
                                    className={`aspect-square border-2 transition-transform hover:scale-110 ${tileColor.hex === c.hex ? "border-zinc-950 ring-2 ring-zinc-950 ring-offset-1" : "border-zinc-200"}`}
                                    style={{ background: c.hex }}
                                />
                            ))}
                        </div>
                        <p className="text-[11px] mt-3 text-zinc-500">Gekozen: <strong>{tileColor.name}</strong></p>
                    </section>

                    {/* Tekstkleur */}
                    <section className="border border-zinc-200 bg-white p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Palette size={16} strokeWidth={1.5} />
                            <p className="label-tiny">Stap 04 — Tekstkleur (Metamark M4 vinyl)</p>
                        </div>

                        {/* Mat / Glans toggle */}
                        <div className="flex items-center gap-2 mb-4">
                            <p className="label-tiny mr-1">Afwerking:</p>
                            {["mat", "glans"].map(f => (
                                <button
                                    key={f}
                                    type="button"
                                    data-testid={`tile-finish-${f}`}
                                    onClick={() => setTextFinish(f)}
                                    className={`px-4 py-1.5 text-xs font-bold uppercase tracking-widest border transition-colors ${
                                        textFinish === f
                                            ? "bg-zinc-950 text-white border-zinc-950"
                                            : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                                    }`}>
                                    {f === "mat" ? "Mat" : "Glanzend"}
                                </button>
                            ))}
                            <span className="text-[11px] text-zinc-500 ml-2">
                                {textFinish === "mat" ? "Vlakke, niet-reflecterende afwerking" : "Lichte glans, levendige kleuren"}
                            </span>
                        </div>

                        <div className="grid grid-cols-5 sm:grid-cols-9 gap-2">
                            {config.text_colors.map(c => (
                                <button
                                    key={c.hex}
                                    type="button"
                                    data-testid={`tile-textcolor-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => setTextColor(c)}
                                    title={c.name}
                                    className={`aspect-square border-2 transition-transform hover:scale-110 ${textColor.hex === c.hex ? "border-zinc-950 ring-2 ring-zinc-950 ring-offset-1" : "border-zinc-200"}`}
                                    style={{ background: c.hex }}
                                />
                            ))}
                        </div>
                        <p className="text-[11px] mt-3 text-zinc-500">
                            Gekozen: <strong>{textColor.name}</strong> · {textFinish === "mat" ? "Mat" : "Glanzend"} afwerking
                        </p>
                    </section>

                    {/* Notitie */}
                    <section className="border border-zinc-200 bg-white p-6">
                        <p className="label-tiny mb-3">Notitie voor ons (optioneel)</p>
                        <input
                            data-testid="tile-notes"
                            type="text"
                            className="input-flat"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value.slice(0, 200))}
                            placeholder="Bv. ander lettertype, layout-wens, datum voor de verjaardag..."
                            maxLength={200}
                        />
                    </section>

                    {/* Add to cart */}
                    <section className="border border-zinc-950 bg-white p-6 flex flex-wrap items-end gap-6 justify-between">
                        <div>
                            <p className="label-tiny">Totaal</p>
                            <p className="font-display text-4xl mt-1">€ {totalPrice}</p>
                            <p className="text-[11px] text-zinc-500 mt-1">€ {config.price.toFixed(2)} × {qty} stuk{qty > 1 ? "s" : ""} · incl. standaard</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="inline-flex items-center border border-zinc-200">
                                <button type="button" data-testid="tile-qty-minus" onClick={() => setQty(q => Math.max(1, q - 1))}
                                    className="px-3 py-3 hover:bg-zinc-50"><Minus size={14} /></button>
                                <span data-testid="tile-qty-value" className="px-4 font-mono-dm font-bold">{qty}</span>
                                <button type="button" data-testid="tile-qty-plus" onClick={() => setQty(q => Math.min(99, q + 1))}
                                    className="px-3 py-3 hover:bg-zinc-50"><Plus size={14} /></button>
                            </div>
                            <button
                                data-testid="tile-add-to-cart"
                                disabled={adding || !text.trim()}
                                onClick={addToCart}
                                className="btn-primary inline-flex items-center gap-2 px-6 py-3.5"
                            >
                                {adding ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
                                In winkelmandje
                            </button>
                        </div>
                    </section>

                    {!user && (
                        <p className="text-xs text-zinc-500 text-center">
                            Nog geen account? <Link to="/register" className="underline">Registreer</Link> of <Link to="/login" className="underline">log in</Link> om te bestellen.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
